import React, { useEffect, useState } from "react";
import { BrandLogo } from "../components/brand/BrandLogo";
import { useNavigate, Link } from "react-router-dom";
import { Mail, Lock, User, ArrowRight, ShieldCheck, Sparkles, Wallet, Loader2, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { pushSignUp } from "../lib/signupEvent";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Une erreur est survenue. Réessayez.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function pwScore(pw) {
  let s = 0;
  if (!pw) return 0;
  if (pw.length >= 8) s++;
  if (pw.length >= 12) s++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
  if (/\d/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return Math.min(s, 4);
}
const PW_LABELS = ["Très faible", "Faible", "Moyen", "Bon", "Excellent"];
const PW_COLORS = ["bg-red-400", "bg-red-400", "bg-amber-400", "bg-brand-lime", "bg-emerald-500"];

function PasswordMeter({ value }) {
  if (!value) return null;
  const score = pwScore(value);
  return (
    <div className="mt-1.5" data-testid="pw-meter">
      <div className="flex gap-1.5">
        {[0, 1, 2, 3].map((i) => (
          <span key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i < score ? PW_COLORS[score] : "bg-brand-soft"}`} />
        ))}
      </div>
      <p className="text-xs text-brand-green/50 mt-1" data-testid="pw-meter-label">
        Robustesse : <span className="font-semibold text-brand-greenDeep">{PW_LABELS[score]}</span>
        {value.length < 8 && " · 8 caractères minimum"}
      </p>
    </div>
  );
}


function GoogleIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/>
      <path fill="#EA4335" d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.46 14.97.5 12 .5A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.3 9.14 4.75 12 4.75z"/>
    </svg>
  );
}

const inputCls = "w-full pl-12 pr-4 py-3.5 rounded-2xl border border-brand-soft focus:border-brand-lime focus:ring-2 focus:ring-brand-lime/30 outline-none transition-all";

export default function LoginPage() {
  const navigate = useNavigate();
  const { loginWithGoogle, loginWithPassword, register, resendVerification, forgotPassword, user, loading } = useAuth();
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [demoLink, setDemoLink] = useState(null);
  const [needVerify, setNeedVerify] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/app", { replace: true });
  }, [user, loading, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setDemoLink(null); setNeedVerify(false);
    try {
      if (mode === "login") {
        await loginWithPassword(email.trim(), password);
        navigate("/app", { replace: true });
      } else if (mode === "register") {
        const r = await register({ email: email.trim(), password, name: name.trim() });
        if (r?.is_new) pushSignUp("email", r.user_id);
        if (r.email_verified) {
          toast.success("Compte prêt. Connectez-vous.");
          setMode("login"); setPassword("");
        } else {
          toast.success("Compte créé ! Vérifiez votre e-mail pour l'activer.");
          setNeedVerify(true);
          if (r.demo_verification_link) setDemoLink({ type: "verify", url: r.demo_verification_link });
        }
      } else if (mode === "forgot") {
        const r = await forgotPassword(email.trim());
        toast.success("Si un compte existe, un e-mail de réinitialisation a été envoyé.");
        if (r.demo_reset_link) setDemoLink({ type: "reset", url: r.demo_reset_link });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === "email_not_verified") {
        setNeedVerify(true);
        toast.error("Votre e-mail n'est pas encore vérifié.");
      } else {
        toast.error(formatApiErrorDetail(detail) || err.message);
      }
    } finally { setBusy(false); }
  };

  const resend = async () => {
    try {
      const r = await resendVerification(email.trim());
      toast.success("E-mail de vérification renvoyé.");
      if (r.demo_verification_link) setDemoLink({ type: "verify", url: r.demo_verification_link });
    } catch { toast.error("Impossible de renvoyer l'e-mail."); }
  };

  const goDemoLink = () => {
    if (!demoLink) return;
    const path = demoLink.url.includes("/verifier-email") ? demoLink.url.split(window.location.origin)[1] || demoLink.url : demoLink.url;
    // navigate relatively when same origin
    try {
      const u = new URL(demoLink.url);
      navigate(u.pathname + u.search);
    } catch { window.location.href = demoLink.url; }
  };

  const titles = { login: "Bon retour !", register: "Créer votre compte", forgot: "Mot de passe oublié" };
  const subtitles = {
    login: "Connectez-vous pour gérer votre portefeuille immobilier.",
    register: "Inscrivez-vous en quelques secondes.",
    forgot: "Saisissez votre e-mail pour recevoir un lien de réinitialisation.",
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex flex-col justify-center px-6 sm:px-16 py-10 bg-white">
        <Link to="/" className="flex items-center gap-2 mb-12">
          <BrandLogo variant="clair" className="h-8 sm:h-9 w-auto" />
        </Link>

        <div className="max-w-sm w-full mx-auto lg:mx-0">
          <h1 className="font-display text-4xl font-semibold text-brand-greenDeep" data-testid="auth-title">{titles[mode]}</h1>
          <p className="mt-2 text-brand-green/60">{subtitles[mode]}</p>

          {mode !== "forgot" && (
            <>
              <button onClick={loginWithGoogle} data-testid="google-login-btn"
                className="mt-8 w-full flex items-center justify-center gap-3 py-3.5 rounded-full border border-brand-soft hover:border-brand-lime hover:bg-brand-softer font-medium text-brand-green transition-colors">
                <GoogleIcon /> Continuer avec Google
              </button>
              <div className="flex items-center gap-4 my-6">
                <div className="flex-1 h-px bg-brand-soft" />
                <span className="text-sm text-brand-green/40">ou</span>
                <div className="flex-1 h-px bg-brand-soft" />
              </div>
            </>
          )}

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-brand-green/40" />
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nom complet" className={inputCls} data-testid="auth-name" />
              </div>
            )}
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-brand-green/40" />
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Adresse email" className={inputCls} data-testid="auth-email" />
            </div>
            {mode !== "forgot" && (
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-brand-green/40" />
                <input type={showPassword ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} placeholder={mode === "register" ? "Mot de passe (8 caractères min.)" : "Mot de passe"} className={`${inputCls} pr-12`} data-testid="auth-password" />
                <button type="button" onClick={() => setShowPassword((v) => !v)} aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"} className="absolute right-4 top-1/2 -translate-y-1/2 text-brand-green/40 hover:text-brand-greenDeep transition-colors" data-testid="toggle-password-visibility">
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            )}
            {mode === "register" && <PasswordMeter value={password} />}
            <button type="submit" disabled={busy}
              className="group w-full flex items-center justify-center gap-2 py-3.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold transition-colors disabled:opacity-60" data-testid="auth-submit">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{mode === "login" ? "Se connecter" : mode === "register" ? "Créer mon compte" : "Envoyer le lien"}<ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" /></>}
            </button>
          </form>

          {needVerify && (
            <div className="mt-4 rounded-2xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800" data-testid="verify-notice">
              Vérifiez votre e-mail pour activer votre compte. <button onClick={resend} className="underline font-semibold" data-testid="resend-verify">Renvoyer l'e-mail</button>
            </div>
          )}
          {demoLink && (
            <button onClick={goDemoLink} className="mt-3 w-full text-center text-sm font-semibold text-brand-limeDark underline" data-testid="demo-link-btn">
              {demoLink.type === "verify" ? "Mode démo : vérifier mon e-mail maintenant →" : "Mode démo : réinitialiser mon mot de passe →"}
            </button>
          )}

          <div className="mt-6 text-sm text-brand-green/60 text-center space-y-1">
            {mode === "login" && (
              <>
                <p><button onClick={() => { setMode("forgot"); setDemoLink(null); }} className="hover:text-brand-greenDeep" data-testid="to-forgot">Mot de passe oublié ?</button></p>
                <p>Pas encore de compte ? <button onClick={() => { setMode("register"); setDemoLink(null); }} className="font-semibold text-brand-greenDeep" data-testid="to-register">Inscrivez-vous gratuitement</button></p>
              </>
            )}
            {mode === "register" && <p>Déjà un compte ? <button onClick={() => { setMode("login"); setDemoLink(null); }} className="font-semibold text-brand-greenDeep" data-testid="to-login">Se connecter</button></p>}
            {mode === "forgot" && <p><button onClick={() => { setMode("login"); setDemoLink(null); }} className="font-semibold text-brand-greenDeep" data-testid="back-to-login">← Retour à la connexion</button></p>}
          </div>
        </div>
      </div>

      <div className="hidden lg:flex flex-col justify-center bg-brand-greenDeep relative overflow-hidden px-16">
        <div className="absolute top-10 right-10 w-80 h-80 bg-brand-lime/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-brand-lime/10 rounded-full blur-3xl" />
        <div className="relative max-w-md">
          <h2 className="font-display text-4xl font-semibold text-white leading-tight">La gestion locative, réinventée par l'IA.</h2>
          <p className="mt-4 text-white/70">Automatisez vos quittances, suivez vos finances et laissez l'assistant IA rédiger vos documents.</p>
          <div className="mt-10 space-y-5">
            {[[ShieldCheck, "Compte 100% sécurisé, accessible 24/7"], [Sparkles, "Assistant IA pour vos documents et relances"], [Wallet, "Suivi financier et aide à la déclaration"]].map(([Icon, txt], i) => (
              <div key={i} className="flex items-center gap-4 text-white/90">
                <span className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0"><Icon className="w-5 h-5 text-brand-lime" /></span>
                <span>{txt}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
